(() => {
  const TWO_PIECE_QUANTITY = '2 pcs (Two seats outer sides)';
  const CUSHION_COVERAGE = Object.freeze({
    single: '1 pc · Driver seat only',
    pair: '2 pcs · Driver + Passenger'
  });
  const LEGACY_CUSHION_COVERAGE = Object.freeze({
    single: '1 pc （Driver only）',
    pair: '2 pcs  (Driver + Passenger)'
  });
  const LOWER_COVERAGE = Object.freeze({
    single: '1 pc · Single outer side',
    pair: '2 pcs · Outer pair',
    four: '4 pcs · Inner + outer'
  });
  const LEGACY_LOWER_QUANTITY = Object.freeze({
    single: '1 pc (Single outer side)',
    pair: TWO_PIECE_QUANTITY,
    four: '4 pcs (Two seats both sides)'
  });
  const UPPER_COVERAGE = Object.freeze({
    single: '1 pc · Single outer side',
    pair: '2 pcs · Outer pair',
    four: '4 pcs · Inner + outer'
  });
  const LEGACY_UPPER_QUANTITY = Object.freeze({
    single: '1 pc (Single outer side)',
    pair: TWO_PIECE_QUANTITY,
    four: '4 pcs (Two seats both sides)'
  });
  const SHOULDER_COVERAGE = Object.freeze({
    single: '1 pc · Single outer side',
    pair: '2 pcs · Outer pair',
    four: '4 pcs · Inner + outer'
  });
  const LEGACY_SHOULDER_QUANTITY = Object.freeze({
    single: '1 pc (Single outer side)',
    pair: TWO_PIECE_QUANTITY,
    four: '4 pcs (Two seats both sides)'
  });
  const SHOULDER_MODEL = Object.freeze({
    M2: 'BMW G87 M2 / G82 M4',
    M3: 'BMW G80 M3',
    M4: 'BMW G87 M2 / G82 M4'
  });
  const SHOULDER_MODEL_SHORT = Object.freeze({
    M2: 'G87 M2 / G82 M4',
    M3: 'G80 M3',
    M4: 'G87 M2 / G82 M4'
  });
  const DOOR_PAIR = Object.freeze({
    fitment: 'BMW G82 M4',
    set: '2 pcs · Left + Right'
  });
  const LEGACY_DOOR_PAIR = Object.freeze({
    fitment: 'G82 M4',
    set: '2 pcs'
  });

  const VEHICLE_RULES = Object.freeze({
    M2: Object.freeze({
      label: 'M2 G87',
      shoulderType: 'Retaining Guide (M2/M4)',
      doorMode: 'contact',
      rearMode: 'hidden',
      fullModules: Object.freeze(['thigh', 'waist', 'shoulder', 'cushion'])
    }),
    M3: Object.freeze({
      label: 'M3 G80',
      shoulderType: 'Standard Routing (M3)',
      doorMode: 'hidden',
      rearMode: 'purchase',
      fullModules: Object.freeze(['thigh', 'waist', 'shoulder', 'cushion'])
    }),
    M4: Object.freeze({
      label: 'M4 G82',
      shoulderType: 'Retaining Guide (M2/M4)',
      doorMode: 'purchase',
      rearMode: 'purchase',
      fullModules: Object.freeze(['thigh', 'waist', 'shoulder', 'door', 'cushion'])
    })
  });

  const REAR_MODEL = Object.freeze({ M3: 'BMW G80 M3', M4: 'BMW G82 M4' });

  const REAR_COLOR_MAP = Object.freeze({
    'Fiona Red': 'Fiona Red/Black',
    'Yas Marina Blue': 'Yas Marina Blue/Black',
    'Silverstone (White)': 'Silverstone/Black',
    'Kyalami Orange': 'Kyalami Orange/Black',
    Tartufo: 'Tartufo',
    Black: 'Black',
    'Ivory White': 'Ivory White',
    'Fjord Blue/Black': 'Fjord Blue/Black'
  });

  const PRODUCT_RULES = {
    thigh: {
      colorOption: 'Color',
      pairSelections: (build) => ([
        { Color: build.color, Coverage: LOWER_COVERAGE.pair },
        { Color: build.color, Quantity: LEGACY_LOWER_QUANTITY.pair }
      ]),
      fourSelections: (build) => ([
        { Color: build.color, Coverage: LOWER_COVERAGE.four },
        { Color: build.color, Quantity: LEGACY_LOWER_QUANTITY.four }
      ]),
      singleSelections: (build) => ([
        { Color: build.color, Coverage: LOWER_COVERAGE.single },
        { Color: build.color, Quantity: LEGACY_LOWER_QUANTITY.single }
      ])
    },
    waist: {
      colorOption: 'Color',
      colorOptions: Object.freeze(['Color', 'Seat Color']),
      pairSelections: (build) => ([
        { Color: build.color, Coverage: UPPER_COVERAGE.pair, 'Mounting Style': build.strapStyle },
        { 'Seat Color': build.color, 'Install Style': build.strapStyle, Quantity: LEGACY_UPPER_QUANTITY.pair }
      ]),
      fourSelections: (build) => ([
        { Color: build.color, Coverage: UPPER_COVERAGE.four, 'Mounting Style': build.strapStyle },
        { 'Seat Color': build.color, 'Install Style': build.strapStyle, Quantity: LEGACY_UPPER_QUANTITY.four }
      ]),
      singleSelections: (build) => ([
        { Color: build.color, Coverage: UPPER_COVERAGE.single, 'Mounting Style': build.strapStyle },
        { 'Seat Color': build.color, 'Install Style': build.strapStyle, Quantity: LEGACY_UPPER_QUANTITY.single }
      ])
    },
    shoulder: {
      colorOption: 'Color',
      colorOptions: Object.freeze(['Color', 'Seat Color']),
      pairSelections: (build) => ([
        { 'BMW Model': SHOULDER_MODEL[build.model] || '', Color: build.color, Coverage: SHOULDER_COVERAGE.pair },
        { 'BMW Model': SHOULDER_MODEL_SHORT[build.model] || '', Color: build.color, Coverage: SHOULDER_COVERAGE.pair },
        { 'Seat Color': build.color, Type: VEHICLE_RULES[build.model]?.shoulderType || '', Quantity: LEGACY_SHOULDER_QUANTITY.pair }
      ]),
      fourSelections: (build) => ([
        { 'BMW Model': SHOULDER_MODEL[build.model] || '', Color: build.color, Coverage: SHOULDER_COVERAGE.four },
        { 'BMW Model': SHOULDER_MODEL_SHORT[build.model] || '', Color: build.color, Coverage: SHOULDER_COVERAGE.four },
        { 'Seat Color': build.color, Type: VEHICLE_RULES[build.model]?.shoulderType || '', Quantity: LEGACY_SHOULDER_QUANTITY.four }
      ]),
      singleSelections: (build) => ([
        { 'BMW Model': SHOULDER_MODEL[build.model] || '', Color: build.color, Coverage: SHOULDER_COVERAGE.single },
        { 'BMW Model': SHOULDER_MODEL_SHORT[build.model] || '', Color: build.color, Coverage: SHOULDER_COVERAGE.single },
        { 'Seat Color': build.color, Type: VEHICLE_RULES[build.model]?.shoulderType || '', Quantity: LEGACY_SHOULDER_QUANTITY.single }
      ])
    },
    door: {
      colorOption: 'Interior Color',
      colorOptions: Object.freeze(['Interior Color', 'Color']),
      pairSelections: (build) => ([
        { 'Interior Color': build.color, Fitment: build.model === 'M4' ? DOOR_PAIR.fitment : '', Set: DOOR_PAIR.set },
        { Color: build.color, Option: build.model === 'M4' ? LEGACY_DOOR_PAIR.fitment : '', Quantity: LEGACY_DOOR_PAIR.set }
      ]),
      fourSelections: null,
      singleSelections: null,
      // 2026-10-19 Shopify 专员：门把手 10-11 起不计入套装件数、不享套装折扣（与后台四档自动折扣一致）
      discountEligible: false
    },
    cushion: {
      colorOption: 'Color',
      colorOptions: Object.freeze(['Color', 'Seat Color']),
      pairSelections: (build) => ([
        { Color: build.color, Coverage: CUSHION_COVERAGE.pair },
        { 'Seat Color': build.color, Coverage: LEGACY_CUSHION_COVERAGE.pair }
      ]),
      fourSelections: null,
      singleSelections: (build) => ([
        { Color: build.color, Coverage: CUSHION_COVERAGE.single },
        { 'Seat Color': build.color, Coverage: LEGACY_CUSHION_COVERAGE.single }
      ])
    },
    rear: {
      colorOption: 'Seat Color',
      discountEligible: false,
      pieceCount: 1,
      pairSelections: (build) => ([
        { Model: REAR_MODEL[build.model] || '', 'Seat Color': REAR_COLOR_MAP[build.color] || '' },
        { 'Seat Color': REAR_COLOR_MAP[build.color] || '' }
      ]),
      fourSelections: null,
      singleSelections: null
    },
    armrest: {
      colorOption: 'Interior Color',
      colorOptions: Object.freeze(['Interior Color', 'Color']),
      discountEligible: false,
      pieceCount: 1,
      pairSelections: (build) => ([
        { 'Steering Position': build.armrestSide, 'Interior Color': build.color, Design: build.armrestDesign || 'Script Embossed \u00b7 Most Popular' },
        { 'Pattern Type': build.armrestSide, Color: build.color }
      ]),
      fourSelections: null,
      singleSelections: null
    }
  };

  const PRESETS = {
    entry: ['thigh'],
    core: ['thigh', 'waist'],
    three: ['thigh', 'waist', 'shoulder']
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const isM2OnlyColor = (color) => /^(?:too?l?box\s*\/\s*)?m2$/i.test(String(color || '').trim())
    || /^too?l?box\s*\/\s*m2$/i.test(String(color || '').trim());

  const colorDisplayName = (color) => isM2OnlyColor(color) ? 'M2' : color;

  const moduleMode = (key, model) => {
    if (key === 'door') return VEHICLE_RULES[model]?.doorMode || 'hidden';
    if (key === 'rear') return VEHICLE_RULES[model]?.rearMode || 'hidden';
    return 'purchase';
  };

  const isDiscountEligible = (key) => PRODUCT_RULES[key]?.discountEligible !== false;

  class SeatProtectionBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        const source = this.querySelector('[data-builder-products]');
        if (!source) throw new Error('Builder product data is missing.');

        this.config = JSON.parse(source.textContent);
        this.products = new Map(this.config.products.map((product) => [product.key, product]));
        if (![...this.products.values()].some((product) => product.configured)) {
          throw new Error('No seat-protection products are configured.');
        }

        this.currency = this.config.currency || 'USD';
        this.nextBuildId = 1;
        this.state = {
          builds: [this.createBuild()],
          activeId: 1
        };

        this.cacheElements();
        this.bindEvents();
        this.ui.hidden = false;
        this.render();
        this.setupMobileBarVisibility();
        this.setAttribute('data-ready', '');
      } catch (error) {
        this.removeAttribute('data-ready');
        if (this.ui) this.ui.hidden = true;
        this.showFallbackError(error.message);
      }
    }

    createBuild(seed = {}) {
      const build = {
        id: this.nextBuildId,
        configurationId: seed.configurationId || this.createConfigurationId(),
        model: seed.model || 'M2',
        color: seed.color || '',
        strapStyle: seed.strapStyle || 'Strap Tucked',
        armrestSide: seed.armrestSide || '',
        armrestDesign: seed.armrestDesign || 'Script Embossed \u00b7 Most Popular',
        modules: new Set(seed.modules || PRESETS.core),
        coverageByModule: {
          thigh: 2,
          waist: 2,
          shoulder: 2,
          ...(seed.coverageByModule || {})
        },
        quantity: seed.quantity || 1
      };
      this.nextBuildId += 1;
      return build;
    }

    createConfigurationId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `cx-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    cacheElements() {
      this.ui = this.querySelector('[data-builder-ui]');
      this.mobileBar = this.querySelector('[data-mobile-bar]');
      this.mobileBarStart = this.querySelector('[data-mobile-bar-start]');
      this.mobileBarEnd = this.querySelector('[data-mobile-bar-end]');
      this.buildTabs = this.querySelector('[data-build-tabs]');
      this.addBuildButton = this.querySelector('[data-add-build]');
      this.colorOptions = this.querySelector('[data-color-options]');
      this.colorName = this.querySelector('[data-color-name]');
      this.colorPreviewImage = this.querySelector('[data-color-preview-image]');
      this.colorPreviewPlaceholder = this.querySelector('[data-color-preview-placeholder]');
      this.moduleInputs = [...this.querySelectorAll('[data-module-input]')];
      this.strapStyle = this.querySelector('[data-strap-style]');
      this.strapInputs = [...this.querySelectorAll('[data-strap-input]')];
      this.summaryBuilds = this.querySelector('[data-summary-builds]');
      this.baseTotal = this.querySelector('[data-base-total]');
      this.coverageSavings = this.querySelector('[data-coverage-savings]');
      this.coverageSavingsRow = this.querySelector('[data-coverage-savings-row]');
      this.groupSavings = this.querySelector('[data-group-savings]');
      this.groupSavingsRow = this.querySelector('[data-group-savings-row]');
      this.finalTotal = this.querySelector('[data-final-total]');
      this.saveLine = this.querySelector('[data-save-line]');
      this.surchargeNote = this.querySelector('[data-surcharge-note]');
      this.validationMessage = this.querySelector('[data-validation-message]');
      this.checkoutNote = this.querySelector('[data-checkout-note]');
      this.errorBox = this.querySelector('[data-builder-error]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.buyButton = this.querySelector('[data-buy-now]');
      this.mobileAddButton = this.querySelector('[data-mobile-add]');
      this.mobileSummary = this.querySelector('[data-mobile-summary]');
      this.mobileTotal = this.querySelector('[data-mobile-total]');
      this.m2DoorContact = this.querySelector('[data-m2-door-contact]');
      this.doorNote = this.querySelector('[data-door-note]');
      this.shoulderNote = this.querySelector('[data-shoulder-note]');
      this.rearNote = this.querySelector('[data-rear-note]');
      this.armrestNote = this.querySelector('[data-armrest-note]');
      this.armrestSide = this.querySelector('[data-armrest-side]');
      this.armrestSideInputs = [...this.querySelectorAll('[data-armrest-side-input]')];
      this.armrestDesignInputs = [...this.querySelectorAll('[data-armrest-design-input]')];
      this.fullPresetCopy = this.querySelector('[data-full-preset-copy]');
    }

    bindEvents() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.colorPreviewImage?.addEventListener('error', () => {
        this.colorPreviewFailedUrl = this.colorPreviewImage.getAttribute('src') || '';
        this.showColorPreviewFallback(`Preview image unavailable for ${colorDisplayName(this.activeBuild.color)}. Your color selection is saved.`);
      });
    }

    setupMobileBarVisibility() {
      this.mobileBar.hidden = true;
      this.mobileBarMedia = window.matchMedia('(max-width: 767px)');

      if (!('IntersectionObserver' in window) || !this.mobileBarStart || !this.mobileBarEnd || !this.addButton) {
        return;
      }

      this.mobileBarVisibilityReady = true;
      this.mobileBarVisibilityHandler = () => this.syncMobileBarVisibility();
      this.mobileBarObserver = new IntersectionObserver(this.mobileBarVisibilityHandler, {
        threshold: [0, 0.01, 1]
      });
      [this, this.mobileBarStart, this.addButton, this.mobileBarEnd].forEach((element) => {
        this.mobileBarObserver.observe(element);
      });

      if (this.mobileBarMedia.addEventListener) {
        this.mobileBarMedia.addEventListener('change', this.mobileBarVisibilityHandler);
      }
      else {
        this.mobileBarMedia.addListener(this.mobileBarVisibilityHandler);
      }
      window.addEventListener('resize', this.mobileBarVisibilityHandler, { passive: true });
      this.syncMobileBarVisibility();
    }

    syncMobileBarVisibility() {
      if (!this.mobileBar) return;

      if (!this.mobileBarVisibilityReady || !this.mobileBarMedia.matches || this.busy || !this.orderIsValid) {
        this.mobileBar.hidden = true;
        return;
      }

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const builderRect = this.getBoundingClientRect();
      const startRect = this.mobileBarStart.getBoundingClientRect();
      const primaryCtaRect = this.addButton.getBoundingClientRect();
      const endRect = this.mobileBarEnd.getBoundingClientRect();
      const builderIsRelevant = builderRect.bottom > 0 && builderRect.top < viewportHeight;
      const builderHasStarted = startRect.bottom < 0;
      const builderEndIsOutside = endRect.top >= viewportHeight;
      const primaryCtaIsVisible = primaryCtaRect.bottom > 0 && primaryCtaRect.top < viewportHeight;

      this.mobileBar.hidden = !(
        builderIsRelevant
        && builderHasStarted
        && builderEndIsOutside
        && !primaryCtaIsVisible
      );
    }

    get activeBuild() {
      return this.state.builds.find((build) => build.id === this.state.activeId) || this.state.builds[0];
    }

    onChange(event) {
      const build = this.activeBuild;

      if (event.target.matches('[data-vehicle]')) {
        build.model = event.target.value;
        [...build.modules].forEach((key) => {
          if (moduleMode(key, build.model) !== 'purchase') build.modules.delete(key);
        });
        const colors = this.getAvailableColors(build);
        if (!colors.includes(build.color)) build.color = '';
        this.m2DoorContact.hidden = true;
        this.render();
        return;
      }

      if (event.target.matches('[data-color-input]')) {
        build.color = event.target.value;
        ['rear', 'armrest'].forEach((key) => {
          if (build.modules.has(key) && !this.isColorAvailableForModule(key, build)) {
            build.modules.delete(key);
            if (key === 'armrest') build.armrestSide = '';
          }
        });
        this.render();
        return;
      }

      if (event.target.matches('[data-module-input]')) {
        const moduleKey = event.target.value;
        const mode = moduleMode(moduleKey, build.model);
        if (mode !== 'purchase') {
          event.target.checked = false;
          if (mode === 'contact') this.revealM2DoorContact();
          return;
        }

        if (event.target.checked) {
          build.modules.add(moduleKey);
          if (PRODUCT_RULES[moduleKey]?.fourSelections && !build.coverageByModule[moduleKey]) {
            build.coverageByModule[moduleKey] = 2;
          }
          if (moduleKey === 'armrest' && !build.armrestSide) build.armrestSide = 'Left-Hand Drive (LHD)';
        }
        else {
          build.modules.delete(moduleKey);
          if (PRODUCT_RULES[moduleKey]?.fourSelections) build.coverageByModule[moduleKey] = 2;
          if (moduleKey === 'armrest') build.armrestSide = '';
        }
        this.render();
        return;
      }

      if (event.target.matches('[data-strap-input]')) {
        build.strapStyle = event.target.value;
        this.render();
        return;
      }

      if (event.target.matches('[data-armrest-side-input]')) {
        build.armrestSide = event.target.value;
        build.modules.add('armrest');
        this.render();
      }

      if (event.target.matches('[data-armrest-design-input]')) {
        build.armrestDesign = event.target.value;
        this.render();
      }
    }

    onClick(event) {
      const presetButton = event.target.closest('[data-preset]');
      if (presetButton) {
        this.applyPreset(presetButton.dataset.preset);
        return;
      }

      const mapButton = event.target.closest('[data-map-toggle]');
      if (mapButton) {
        this.toggleModule(mapButton.dataset.mapToggle);
        return;
      }

      const coverageButton = event.target.closest('[data-coverage-option]');
      if (coverageButton) {
        const moduleKey = coverageButton.dataset.moduleKey;
        const coverage = Number(coverageButton.dataset.coverageOption);
        if (this.activeBuild.modules.has(moduleKey) && PRODUCT_RULES[moduleKey]?.fourSelections && [2, 4].includes(coverage)) {
          this.activeBuild.coverageByModule[moduleKey] = coverage;
          this.render();
        }
        return;
      }

      const doorCard = event.target.closest('[data-module-card="door"]');
      if (doorCard && VEHICLE_RULES[this.activeBuild.model]?.doorMode === 'contact') {
        event.preventDefault();
        this.revealM2DoorContact();
        return;
      }

      const buildTab = event.target.closest('[data-build-tab]');
      if (buildTab) {
        this.state.activeId = Number(buildTab.dataset.buildTab);
        this.m2DoorContact.hidden = true;
        this.render();
        return;
      }

      const removeBuild = event.target.closest('[data-remove-build]');
      if (removeBuild) {
        event.stopPropagation();
        this.removeBuild(Number(removeBuild.dataset.removeBuild));
        return;
      }

      if (event.target.closest('[data-add-build]')) {
        this.addBuild();
        return;
      }

      if (event.target.closest('[data-add-to-cart]') || event.target.closest('[data-mobile-add]')) {
        this.submit(false);
        return;
      }

      if (event.target.closest('[data-buy-now]')) this.submit(true);
    }

    applyPreset(preset) {
      const build = this.activeBuild;
      const modules = this.presetModules(preset, build);
      const selectedAddOns = [...build.modules].filter((key) => (
        !isDiscountEligible(key) && moduleMode(key, build.model) === 'purchase'
      ));

      build.modules = new Set([
        ...modules.filter((key) => this.products.get(key)?.configured),
        ...selectedAddOns
      ]);
      Object.keys(build.coverageByModule).forEach((key) => {
        build.coverageByModule[key] = 2;
      });
      this.m2DoorContact.hidden = true;
      this.render();
    }

    presetModules(preset, build) {
      if (preset === 'full') {
        return [...(VEHICLE_RULES[build.model]?.fullModules || [])];
      }
      return PRESETS[preset] || [];
    }

    toggleModule(moduleKey) {
      const build = this.activeBuild;
      const mode = moduleMode(moduleKey, build.model);
      if (mode !== 'purchase') {
        if (mode === 'contact') this.revealM2DoorContact();
        return;
      }

      const product = this.products.get(moduleKey);
      if (!product?.configured) return;
      if (build.modules.has(moduleKey)) {
        build.modules.delete(moduleKey);
        if (PRODUCT_RULES[moduleKey]?.fourSelections) build.coverageByModule[moduleKey] = 2;
        if (moduleKey === 'armrest') build.armrestSide = '';
      }
      else {
        build.modules.add(moduleKey);
        if (PRODUCT_RULES[moduleKey]?.fourSelections && !build.coverageByModule[moduleKey]) {
          build.coverageByModule[moduleKey] = 2;
        }
        if (moduleKey === 'armrest' && !build.armrestSide) build.armrestSide = 'Left-Hand Drive (LHD)';
      }
      this.render();
    }

    revealM2DoorContact() {
      this.m2DoorContact.hidden = false;
      this.m2DoorContact.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    addBuild() {
      const current = this.activeBuild;
      const build = this.createBuild({ model: current.model, color: current.color });
      this.state.builds.push(build);
      this.state.activeId = build.id;
      this.m2DoorContact.hidden = true;
      this.render();
    }

    removeBuild(id) {
      if (this.state.builds.length === 1) return;
      const index = this.state.builds.findIndex((build) => build.id === id);
      this.state.builds = this.state.builds.filter((build) => build.id !== id);
      if (this.state.activeId === id) {
        const replacement = this.state.builds[Math.min(index, this.state.builds.length - 1)];
        this.state.activeId = replacement.id;
      }
      this.render();
    }

    render() {
      const build = this.activeBuild;
      this.renderBuildTabs();
      this.renderVehicle(build);
      this.renderColorOptions(build);
      this.renderModules(build);
      this.renderPresetState(build);
      this.renderCoverageMap(build);
      this.renderStrapStyle(build);
      this.renderArmrestSide(build);
      this.renderSummary();
    }

    renderBuildTabs() {
      this.buildTabs.innerHTML = this.state.builds.map((build, index) => {
        const active = build.id === this.state.activeId;
        const color = colorDisplayName(build.color) || 'No color';
        const pieces = this.physicalPieces(build);
        const remove = this.state.builds.length > 1
          ? `<button type="button" data-remove-build="${build.id}" aria-label="Remove Set ${index + 1}">×</button>`
          : '';
        return `
          <div class="cx-seat-builder__build-tab${active ? ' is-active' : ''}">
            <button type="button" data-build-tab="${build.id}" aria-current="${active ? 'true' : 'false'}">
              <strong>Set ${index + 1}</strong>
              <span>${escapeHtml(VEHICLE_RULES[build.model]?.label || build.model)} · ${escapeHtml(color)} · ${pieces} pcs${build.quantity > 1 ? ` · Qty ${build.quantity}` : ''}</span>
            </button>
            ${remove}
          </div>`;
      }).join('');
    }

    renderVehicle(build) {
      this.querySelectorAll('[data-vehicle]').forEach((input) => {
        input.checked = input.value === build.model;
      });
      this.fullPresetCopy.textContent = build.model === 'M4'
        ? 'Lower bolsters + upper bolsters + shoulder & belt area + seat cushion + door pulls'
        : 'Lower bolsters + upper bolsters + shoulder & belt area + seat cushion';
      if (this.doorNote) {
        this.doorNote.textContent = build.model === 'M4'
          ? 'M4 only · Left + Right Pair · Not part of the set discount'
          : 'G87 M2 made-to-order request';
      }
      if (this.shoulderNote) {
        this.shoulderNote.textContent = build.model === 'M3'
          ? 'Standard seat belt routing for G80 M3'
          : 'Fits around the M2/M4 seat belt retaining guide';
      }
      if (this.rearNote) {
        this.rearNote.textContent = build.color === 'CS'
          ? 'Rear Seat Base Protector is not currently available in CS. Your front-seat choices remain available.'
          : 'Seat cushion and inner and outer base bolsters';
      }
      if (this.armrestNote) {
        this.armrestNote.textContent = build.color === 'CS'
          ? 'Center armrest cover is not available for CS.'
          : 'Matching Merino leather cover in your selected seat color.';
      }
    }

    renderColorOptions(build) {
      const colors = this.getAvailableColors(build);
      const previews = colors.map((color) => ({
        color,
        ...this.getColorPreview(color, build)
      }));
      const optionSignature = `${build.model}:${previews.map(({ color, thumbnail }) => `${color}:${thumbnail}`).join('|')}`;

      if (this.colorOptions.dataset.optionSignature !== optionSignature) {
        this.colorOptions.innerHTML = previews.map(({ color, thumbnail }, index) => {
          const displayName = colorDisplayName(color);
          const swatch = thumbnail
            ? `<img src="${escapeHtml(thumbnail)}" alt="" width="112" height="112" loading="lazy" decoding="async">`
            : `<span aria-hidden="true">${escapeHtml(displayName.slice(0, 2))}</span>`;
          return `
            <label class="cx-seat-builder__color-swatch" title="${escapeHtml(displayName)}">
              <input type="radio" name="${escapeHtml(this.id)}-seat-color" value="${escapeHtml(color)}" data-color-input>
              <span class="cx-seat-builder__color-swatch-image">${swatch}</span>
              <span class="sr-only">${escapeHtml(displayName)}</span>
            </label>`;
        }).join('');
        this.colorOptions.dataset.optionSignature = optionSignature;
      }

      if (!colors.includes(build.color)) build.color = '';
      this.colorOptions.querySelectorAll('[data-color-input]').forEach((input) => {
        input.checked = input.value === build.color;
      });

      const displayName = colorDisplayName(build.color) || 'Select a color';
      this.colorName.textContent = displayName;
      const preview = build.color ? this.getColorPreview(build.color, build) : null;
      if (preview?.image) {
        if (this.colorPreviewFailedUrl === preview.image) {
          this.showColorPreviewFallback(`Preview image unavailable for ${displayName}. Your color selection is saved.`);
          return;
        }
        if (this.colorPreviewImage.getAttribute('src') !== preview.image) {
          this.colorPreviewFailedUrl = '';
          this.colorPreviewImage.src = preview.image;
        }
        this.colorPreviewImage.alt = `BMW M Carbon bucket seat in ${displayName}`;
        this.colorPreviewImage.hidden = false;
        this.colorPreviewPlaceholder.hidden = true;
      }
      else {
        this.showColorPreviewFallback(build.color
          ? `Preview image unavailable for ${displayName}. Your color selection is saved.`
          : 'Choose a color to see the matching seat.');
      }
    }

    showColorPreviewFallback(message) {
      this.colorPreviewImage.hidden = true;
      this.colorPreviewImage.removeAttribute('src');
      this.colorPreviewImage.alt = '';
      const messageContainer = this.colorPreviewPlaceholder.querySelector('span');
      if (messageContainer) messageContainer.textContent = message;
      this.colorPreviewPlaceholder.hidden = false;
    }

    renderModules(build) {
      this.moduleInputs.forEach((input) => {
        const key = input.value;
        const product = this.products.get(key);
        const mode = moduleMode(key, build.model);
        const isContactOnly = mode === 'contact';
        const isHiddenForVehicle = mode === 'hidden';
        const addOnUnavailableForColor = ['rear', 'armrest'].includes(key)
          && Boolean(build.color)
          && !this.isColorAvailableForModule(key, build);
        const disabled = !product?.configured || isHiddenForVehicle || addOnUnavailableForColor;
        const card = input.closest('[data-module-card]');
        const row = input.closest('[data-module-row]');
        const price = this.querySelector(`[data-module-price="${key}"]`);
        const coverageControl = this.querySelector(`[data-module-coverage="${key}"]`);
        const selected = build.modules.has(key);
        const coverage = this.coverageFor(key, build);

        if (row) row.hidden = isHiddenForVehicle;
        input.checked = selected;
        input.disabled = disabled;
        card.classList.toggle('is-selected', selected);
        card.classList.toggle('is-disabled', disabled);
        card.classList.toggle('is-contact-only', isContactOnly);

        if (coverageControl) {
          coverageControl.hidden = !selected || disabled;
          coverageControl.querySelectorAll('[data-coverage-option]').forEach((button) => {
            const active = Number(button.dataset.coverageOption) === coverage;
            button.classList.toggle('is-selected', active);
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        }

        if (!product?.configured) {
          price.textContent = 'Not configured';
          return;
        }
        if (isContactOnly) {
          price.textContent = 'Contact us';
          return;
        }
        if (isHiddenForVehicle) {
          price.textContent = key === 'rear' ? 'M3 only' : 'M4 only';
          return;
        }
        if (!build.color) {
          price.textContent = price.dataset.defaultPrice || 'Choose color';
          return;
        }
        if (key === 'waist' && !build.strapStyle) {
          price.textContent = 'Choose strap style';
          return;
        }
        if (addOnUnavailableForColor) {
          price.textContent = 'Not available in CS';
          return;
        }

        if (key === 'armrest' && !build.armrestSide) {
          const preview = this.resolveVariant(key, { ...build, armrestSide: 'Left-Hand Drive (LHD)' });
          const colorVariant = preview && preview.ok ? preview.variant : this.getAnyColorVariant(key, build);
          if (colorVariant) this.renderModulePrice(price, colorVariant, { ok: false }, 1);
          else price.textContent = 'Choose LHD or RHD';
          return;
        }

        const result = this.resolveVariant(key, build);
        if (!result.ok) {
          price.textContent = 'Unavailable';
          return;
        }
        this.renderModulePrice(price, result.variant, this.resolveSingleVariant(key, build), coverage);
      });
    }

    renderPresetState(build) {
      this.querySelectorAll('[data-preset]').forEach((button) => {
        const key = button.dataset.preset;
        const expected = this.presetModules(key, build);
        const selectedCoreModules = [...build.modules].filter(isDiscountEligible);
        const selected = expected.length === selectedCoreModules.length
          && expected.every((moduleKey) => selectedCoreModules.includes(moduleKey))
          && expected.every((moduleKey) => !PRODUCT_RULES[moduleKey]?.fourSelections || this.coverageFor(moduleKey, build) === 2);
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-full-coverage', selected && this.coverageFor(key, build) === 4);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');

        const priceContainer = button.querySelector('[data-preset-price]');
        if (!priceContainer) return;
        if (!build.color) {
          priceContainer.textContent = 'Choose a color for live price';
          return;
        }

        const previewBuild = {
          ...build,
          strapStyle: build.strapStyle || 'Strap Tucked',
          modules: new Set(expected),
          coverageByModule: {
            ...build.coverageByModule,
            thigh: 2,
            waist: 2,
            shoulder: 2
          },
          quantity: 1
        };
        const calculation = this.calculateBuild(previewBuild, 0);
        this.renderPresetPrice(priceContainer, calculation);
      });
    }

    renderPresetPrice(container, calculation) {
      container.replaceChildren();
      if (!calculation.valid) {
        container.textContent = 'Unavailable in this color';
        return;
      }

      if (calculation.afterCoverageUnit < calculation.baseUnit) {
        const compare = document.createElement('s');
        compare.textContent = this.formatMoney(calculation.baseUnit);
        container.append(compare);
      }

      const price = document.createElement('strong');
      price.textContent = this.formatMoney(calculation.afterCoverageUnit);
      container.append(price);

      if (calculation.afterCoverageUnit < calculation.baseUnit) {
        const saving = document.createElement('span');
        saving.className = 'cx-seat-builder__preset-saving';
        saving.textContent = `Save ${this.formatMoney(calculation.baseUnit - calculation.afterCoverageUnit)}`;
        container.append(saving);
      }
    }

    renderCoverageMap(build) {
      this.querySelectorAll('[data-map-toggle]').forEach((button) => {
        const key = button.dataset.mapToggle;
        const selected = build.modules.has(key);
        const doorMode = VEHICLE_RULES[build.model]?.doorMode;
        const contactOnly = key === 'door' && doorMode === 'contact';
        const hiddenForVehicle = key === 'door' && doorMode === 'hidden';
        const configured = this.products.get(key)?.configured;
        button.hidden = hiddenForVehicle;
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-contact-only', contactOnly);
        button.disabled = !configured;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      this.querySelectorAll('[data-map-zone]').forEach((zone) => {
        const selected = build.modules.has(zone.dataset.mapZone);
        zone.classList.toggle('is-selected', selected);
        zone.classList.toggle('is-full-coverage', selected && this.coverageFor(zone.dataset.mapZone, build) === 4);
        zone.classList.toggle('is-contact-only', zone.dataset.mapZone === 'door' && VEHICLE_RULES[build.model]?.doorMode === 'contact');
      });
    }

    renderStrapStyle(build) {
      const show = build.modules.has('waist');
      this.strapStyle.hidden = !show;
      this.strapInputs.forEach((input) => {
        input.checked = input.value === build.strapStyle;
      });
    }

    renderArmrestSide(build) {
      if (!this.armrestSide) return;
      const product = this.products.get('armrest');
      const colorAvailable = Boolean(build.color) && this.isColorAvailableForModule('armrest', build);
      const armrestOn = build.modules.has('armrest');
      this.armrestSide.hidden = !product?.configured || !armrestOn;
      this.armrestSide.classList.toggle('is-disabled', !colorAvailable);
      this.armrestSideInputs.forEach((input) => {
        input.checked = input.value === (build.armrestSide || 'Left-Hand Drive (LHD)');
        input.disabled = !colorAvailable;
      });
      this.armrestDesignInputs.forEach((input) => {
        input.checked = input.value === (build.armrestDesign || 'Script Embossed \u00b7 Most Popular');
        input.disabled = !colorAvailable || !build.modules.has('armrest');
      });
    }

    renderSummary() {
      const calculations = this.state.builds.map((build, index) => this.calculateBuild(build, index));
      const order = this.calculateOrder(calculations);

      this.summaryBuilds.innerHTML = calculations.map((calculation, index) => {
        const build = calculation.build;
        const pieces = calculation.lines.reduce((sum, line) => sum + line.coverage, 0);
        const selectedItems = calculation.lines
          .map((line) => (line.key === 'armrest'
            ? `${this.moduleLabel(line.key)} (${build.armrestSide === 'Right-Hand Drive (RHD)' ? 'RHD' : 'LHD'}, ${/plain/i.test(build.armrestDesign || '') ? 'Plain' : 'Script Embossed'})`
            : `${this.moduleLabel(line.key)} (${line.coverage} pcs)`))
          .join(', ');
        const status = calculation.valid
          ? this.formatMoney(calculation.afterCoverageUnit)
          : 'Complete selections';
        return `
          <div class="cx-seat-builder__summary-build">
            <div>
              <strong>Set ${index + 1}</strong>
              <span>${escapeHtml(VEHICLE_RULES[build.model]?.label || build.model)} · ${escapeHtml(colorDisplayName(build.color) || 'Color needed')} · ${pieces} pcs per set${build.quantity > 1 ? ` · Qty ${build.quantity}` : ''}</span>
              <small>${escapeHtml(selectedItems || 'Choose at least one item')}</small>
            </div>
            <span>${status}</span>
          </div>`;
      }).join('');

      this.orderIsValid = order.valid;
      if (this.addBuildButton) {
        this.addBuildButton.hidden = this.state.builds.length === 1 && !calculations[0]?.valid;
      }

      if (order.valid) {
        this.baseTotal.textContent = this.formatMoney(order.baseTotal);
        this.baseTotal.classList.toggle('is-discounted', order.totalSavings > 0);
        this.coverageSavings.textContent = `−${this.formatMoney(order.coverageSavings)}`;
        this.groupSavings.textContent = `−${this.formatMoney(order.groupSavings)}`;
        this.finalTotal.textContent = this.formatMoney(order.finalTotal);
        this.coverageSavingsRow.hidden = order.coverageSavings === 0;
        this.groupSavingsRow.hidden = order.groupSavings === 0;
        this.saveLine.hidden = order.totalSavings === 0;
        this.saveLine.textContent = `You save ${this.formatMoney(order.totalSavings)} compared with buying these selected products separately.`;
        this.surchargeNote.hidden = !calculations.some((calculation) => calculation.lines.some((line) => line.coverage === 4));
        this.mobileSummary.textContent = `${order.totalPieces} pcs · ${order.totalBuilds} ${order.totalBuilds === 1 ? 'car set' : 'car sets'}`;
        this.mobileTotal.textContent = this.formatMoney(order.finalTotal);
      } else {
        this.baseTotal.textContent = '—';
        this.baseTotal.classList.remove('is-discounted');
        this.finalTotal.textContent = '—';
        this.coverageSavingsRow.hidden = true;
        this.groupSavingsRow.hidden = true;
        this.saveLine.hidden = true;
        this.surchargeNote.hidden = true;
        this.mobileSummary.textContent = 'Complete your selections';
        this.mobileTotal.textContent = '—';
      }

      const errors = calculations.flatMap((calculation) => calculation.errors);
      this.validationMessage.textContent = errors[0] || 'Every selected item resolves to one exact, available variant.';

      const cartEnabled = Boolean(this.config.cartEnabled);
      const canSubmit = order.valid && cartEnabled && !this.busy;
      this.addButton.disabled = !canSubmit;
      this.buyButton.disabled = !canSubmit;
      this.mobileAddButton.disabled = !canSubmit;
      this.checkoutNote.textContent = cartEnabled
        ? 'Cart and Checkout must show the same set total before payment.'
        : 'Cart submission is off in this private theme until the discount result is verified in Cart and Checkout.';
      this.renderIncentives(calculations);
      this.syncMobileBarVisibility();
    }

    eligiblePiecesAcrossCart(build) {
      return this.state.builds.reduce((sum, b) => {
        const src = build && b.id === build.id ? build : b;
        return sum + [...src.modules].filter((m) => isDiscountEligible(m)).length * (Number(src.quantity) || 1);
      }, 0);
    }

    calculateBuild(build, index) {
      const errors = [];
      const lines = [];

      if (!build.color) errors.push(`Set ${index + 1}: choose a seat color.`);
      if (build.modules.size === 0) errors.push(`Set ${index + 1}: select at least one item.`);
      if (build.modules.has('waist') && !build.strapStyle) errors.push(`Set ${index + 1}: choose the upper bolster mounting style.`);
      if (build.modules.has('armrest') && !build.armrestSide) errors.push(`Set ${index + 1}: choose left-hand drive or right-hand drive for the center armrest cover.`);

      build.modules.forEach((key) => {
        const result = this.resolveVariant(key, build);
        const anchor = this.resolvePairVariant(key, build);
        if (result.ok && anchor.ok) {
          // 2026-10-19 Shopify 专员：后台自动折扣按整件价打折（含 4 片装），页面同样按整件价计，保证页面价 = 购物车价
          const anchorPrice = isDiscountEligible(key) ? Number(result.variant.price) : this.anchorPriceFor(key, build, result.variant, anchor.variant);
          const surcharge = Number(result.variant.price) - anchorPrice;
          if (surcharge < 0) {
            errors.push(`Set ${index + 1}: ${this.moduleLabel(key)} has an invalid coverage upgrade price.`);
          } else {
            lines.push({
              key,
              product: result.product,
              variant: result.variant,
              anchorVariant: anchor.variant,
              anchorPrice,
              coverage: this.coverageFor(key, build),
              surcharge,
              discountEligible: isDiscountEligible(key)
            });
          }
        } else if (result.reason === 'not-configured') {
          errors.push(`Set ${index + 1}: ${this.moduleLabel(key)} is not configured.`);
        } else if (result.reason === 'contact-only') {
          errors.push(`Set ${index + 1}: contact us before adding M2 interior door pull protection.`);
        } else if (!result.incomplete) {
          errors.push(`Set ${index + 1}: no single available ${this.moduleLabel(key).toLowerCase()} variant matches these choices.`);
        }
      });

      const valid = errors.length === 0 && lines.length === build.modules.size && lines.length > 0;
      const discountEligibleLines = lines.filter((line) => line.discountEligible);
      const addOnLines = lines.filter((line) => !line.discountEligible);
      const baseUnit = valid ? lines.reduce((sum, line) => sum + Number(line.variant.price), 0) : 0;
      const anchorBaseUnit = valid ? discountEligibleLines.reduce((sum, line) => sum + line.anchorPrice, 0) : 0;
      const surchargeUnit = valid ? discountEligibleLines.reduce((sum, line) => sum + line.surcharge, 0) : 0;
      const addOnUnit = valid ? addOnLines.reduce((sum, line) => sum + Number(line.variant.price), 0) : 0;
      const totalEligiblePieces = this.eligiblePiecesAcrossCart(build);
      const coverageBasisPoints = discountEligibleLines.length > 0
        ? this.coverageBasisPoints(totalEligiblePieces)
        : 0;
      const afterCoverageAnchorUnit = valid ? this.applyBasisPoints(anchorBaseUnit, coverageBasisPoints) : 0;
      const afterCoverageUnit = afterCoverageAnchorUnit + surchargeUnit + addOnUnit;

      return {
        build,
        lines,
        valid,
        errors,
        baseUnit,
        anchorBaseUnit,
        surchargeUnit,
        addOnUnit,
        discountEligibleLineCount: discountEligibleLines.length,
        afterCoverageAnchorUnit,
        afterCoverageUnit,
        coverageBasisPoints
      };
    }

    calculateOrder(calculations) {
      const valid = calculations.every((calculation) => calculation.valid);
      if (!valid) return { valid: false };

      const totalBuilds = calculations.reduce((sum, calculation) => sum + calculation.build.quantity, 0);
      const discountEligibleBuilds = calculations.reduce((sum, calculation) => (
        sum + (calculation.discountEligibleLineCount > 0 ? calculation.build.quantity : 0)
      ), 0);
      const totalItems = calculations.reduce((sum, calculation) => sum + calculation.lines.length * calculation.build.quantity, 0);
      const totalPieces = calculations.reduce((sum, calculation) => (
        sum + calculation.lines.reduce((lineSum, line) => lineSum + line.coverage, 0) * calculation.build.quantity
      ), 0);
      const baseTotal = calculations.reduce((sum, calculation) => sum + calculation.baseUnit * calculation.build.quantity, 0);
      const anchorBaseTotal = calculations.reduce((sum, calculation) => sum + calculation.anchorBaseUnit * calculation.build.quantity, 0);
      const afterCoverageAnchorTotal = calculations.reduce((sum, calculation) => sum + calculation.afterCoverageAnchorUnit * calculation.build.quantity, 0);
      const surchargeTotal = calculations.reduce((sum, calculation) => sum + calculation.surchargeUnit * calculation.build.quantity, 0);
      const addOnTotal = calculations.reduce((sum, calculation) => sum + calculation.addOnUnit * calculation.build.quantity, 0);
      const groupBasisPoints = this.groupBasisPoints(discountEligibleBuilds);
      const afterGroupAnchorTotal = this.applyBasisPoints(afterCoverageAnchorTotal, groupBasisPoints);
      const finalTotal = afterGroupAnchorTotal + surchargeTotal + addOnTotal;

      return {
        valid: true,
        totalBuilds,
        discountEligibleBuilds,
        totalItems,
        totalPieces,
        baseTotal,
        surchargeTotal,
        addOnTotal,
        coverageSavings: anchorBaseTotal - afterCoverageAnchorTotal,
        groupSavings: afterCoverageAnchorTotal - afterGroupAnchorTotal,
        totalSavings: baseTotal - finalTotal,
        finalTotal,
        groupBasisPoints
      };
    }

    resolveVariant(key, build) {
      const product = this.products.get(key);
      if (!product?.configured) return { ok: false, reason: 'not-configured' };
      if (isM2OnlyColor(build.color) && build.model !== 'M2') {
        return { ok: false, reason: 'unavailable', product };
      }
      const mode = moduleMode(key, build.model);
      if (mode !== 'purchase') {
        return { ok: false, reason: mode === 'contact' ? 'contact-only' : 'unavailable' };
      }

      const rule = PRODUCT_RULES[key];
      const selectionResolver = this.coverageFor(key, build) === 4 ? rule.fourSelections : rule.pairSelections;
      if (!selectionResolver) return { ok: false, reason: 'unavailable', product };
      const requiredSets = this.selectionSets(selectionResolver(build));
      const incomplete = requiredSets.every((required) => Object.values(required).some((value) => !value));
      if (incomplete) return { ok: false, reason: 'incomplete', incomplete: true, product };

      const variants = product.variants.filter((variant) => {
        if (!variant.available) return false;
        const options = new Map(variant.selectedOptions.map((option) => [option.name, option.value]));
        return requiredSets.some((required) => this.optionsMatchExactly(options, required));
      });

      if (variants.length !== 1) {
        return { ok: false, reason: variants.length === 0 ? 'unavailable' : 'ambiguous', product };
      }

      return { ok: true, product, variant: variants[0] };
    }

    resolvePairVariant(key, build) {
      const product = this.products.get(key);
      if (!product?.configured) return { ok: false, reason: 'not-configured' };
      if (isM2OnlyColor(build.color) && build.model !== 'M2') {
        return { ok: false, reason: 'unavailable', product };
      }
      const mode = moduleMode(key, build.model);
      if (mode !== 'purchase') {
        return { ok: false, reason: mode === 'contact' ? 'contact-only' : 'unavailable' };
      }

      const requiredSets = this.selectionSets(PRODUCT_RULES[key].pairSelections(build));
      if (requiredSets.every((required) => Object.values(required).some((value) => !value))) {
        return { ok: false, reason: 'incomplete', incomplete: true, product };
      }

      const variants = product.variants.filter((variant) => {
        if (!variant.available) return false;
        const options = new Map(variant.selectedOptions.map((option) => [option.name, option.value]));
        return requiredSets.some((required) => this.optionsMatchExactly(options, required));
      });

      return variants.length === 1
        ? { ok: true, product, variant: variants[0] }
        : { ok: false, reason: variants.length === 0 ? 'unavailable' : 'ambiguous', product };
    }

    resolveSingleVariant(key, build) {
      const product = this.products.get(key);
      const rule = PRODUCT_RULES[key];
      if (!product?.configured || !rule?.singleSelections) return { ok: false };
      if (isM2OnlyColor(build.color) && build.model !== 'M2') return { ok: false };

      const requiredSets = this.selectionSets(rule.singleSelections(build));
      if (requiredSets.every((required) => Object.values(required).some((value) => !value))) return { ok: false };

      const variants = product.variants.filter((variant) => {
        if (!variant.available) return false;
        const options = new Map(variant.selectedOptions.map((option) => [option.name, option.value]));
        return requiredSets.some((required) => this.optionsMatchExactly(options, required));
      });

      return variants.length === 1 ? { ok: true, variant: variants[0] } : { ok: false };
    }

    renderModulePrice(container, selectedVariant, singleResult, coverage) {
      container.replaceChildren();
      const selectedPrice = Number(selectedVariant.price);
      const singleReference = singleResult.ok ? Number(singleResult.variant.price) * coverage : 0;
      const formatPrice = container.hasAttribute('data-compact-money')
        ? this.formatCompactMoney.bind(this)
        : this.formatMoney.bind(this);

      if (singleResult.ok && singleReference > selectedPrice) {
        const compare = document.createElement('s');
        compare.textContent = formatPrice(singleReference);
        container.append(compare);
      }

      const price = document.createElement('strong');
      price.textContent = formatPrice(selectedPrice);
      container.append(price);

      if (singleResult.ok && singleReference > selectedPrice) {
        const saving = document.createElement('small');
        saving.textContent = `Save ${formatPrice(singleReference - selectedPrice)} vs ${coverage} singles`;
        container.append(saving);
      }
    }

    getAvailableColors(build) {
      const primaryKey = this.getPrimaryColorProductKey(build);
      return primaryKey ? this.getProductColors(primaryKey, build) : [];
    }

    getPrimaryColorProductKey(build) {
      return this.products.get('thigh')?.configured
        ? 'thigh'
        : [...this.products.keys()].find((key) => this.products.get(key)?.configured && moduleMode(key, build.model) === 'purchase');
    }

    getColorPreview(color, build) {
      const primaryKey = this.products.get('cushion')?.configured
        ? 'cushion'
        : this.getPrimaryColorProductKey(build);
      const product = primaryKey ? this.products.get(primaryKey) : null;
      const rule = primaryKey ? PRODUCT_RULES[primaryKey] : null;
      if (!product?.configured || !rule?.colorOption || !color) return { image: '', thumbnail: '' };

      const exactPair = this.resolvePairVariant(primaryKey, { ...build, color });
      const variant = exactPair.ok ? exactPair.variant : product.variants.find((candidate) => {
        if (!candidate.available || !candidate.image) return false;
        const options = new Map(candidate.selectedOptions.map((option) => [option.name, option.value]));
        return options.get(rule.colorOption) === color;
      });

      return {
        image: variant?.image || '',
        thumbnail: variant?.thumbnail || variant?.image || ''
      };
    }

    isColorAvailableForModule(key, build) {
      if (!build.color) return true;
      if (!this.products.get(key)?.configured) return false;
      if (key === 'rear') return this.resolvePairVariant(key, build).ok;
      return this.getProductColors(key, build).includes(build.color);
    }

    getAnyColorVariant(key, build) {
      const product = this.products.get(key);
      const rule = PRODUCT_RULES[key];
      if (!product?.configured || !rule?.colorOption || !build.color) return null;
      return product.variants.find((variant) => {
        if (!variant.available) return false;
        const options = new Map(variant.selectedOptions.map((option) => [option.name, option.value]));
        return options.get(rule.colorOption) === build.color;
      }) || null;
    }

    getProductColors(key, build) {
      const product = this.products.get(key);
      const rule = PRODUCT_RULES[key];
      const colorOptions = rule.colorOptions || [rule.colorOption];
      const requiredSets = this.selectionSets(rule.pairSelections({ ...build, color: '', strapStyle: '' }))
        .map((selection) => {
          const required = { ...selection };
          colorOptions.forEach((optionName) => delete required[optionName]);
          if (key === 'waist') {
            delete required['Mounting Style'];
            delete required['Install Style'];
          }
          if (key === 'armrest') delete required['Pattern Type'];
          return required;
        });

      const colors = [];
      product.variants.forEach((variant) => {
        if (!variant.available) return;
        const options = new Map(variant.selectedOptions.map((option) => [option.name, option.value]));
        const matches = requiredSets.some((required) => Object.entries(required)
          .every(([name, value]) => value && options.get(name) === value));
        const color = colorOptions.map((optionName) => options.get(optionName)).find(Boolean);
        if (matches && color && !(isM2OnlyColor(color) && build.model !== 'M2') && !colors.includes(color)) colors.push(color);
      });
      return colors;
    }

    selectionSets(required) {
      return Array.isArray(required) ? required : [required];
    }

    optionsMatchExactly(options, required) {
      if (options.size !== Object.keys(required).length) return false;
      return Object.entries(required).every(([name, value]) => {
        const actual = options.get(name);
        if (name === 'Color' || name === 'Seat Color') {
          if (isM2OnlyColor(actual) || isM2OnlyColor(value)) {
            return isM2OnlyColor(actual) && isM2OnlyColor(value);
          }
        }
        return actual === value;
      });
    }

    coverageFor(key, build) {
      if (PRODUCT_RULES[key]?.pieceCount) return Number(PRODUCT_RULES[key].pieceCount);
      return PRODUCT_RULES[key]?.fourSelections && Number(build.coverageByModule?.[key]) === 4 ? 4 : 2;
    }

    physicalPieces(build) {
      return [...build.modules].reduce((sum, key) => sum + this.coverageFor(key, build), 0);
    }

    coverageBasisPoints(moduleCount) {
      const clamped = Math.min(5, Number(moduleCount) || 0);
      return Number(this.config.discounts.coverageBasisPoints[String(clamped)] || 0);
    }

    groupBasisPoints(buildCount) {
      return 0;
    }

    applyBasisPoints(cents, basisPoints) {
      return Math.floor((Number(cents) * (10000 - Number(basisPoints))) / 10000);
    }

    anchorPriceFor(key, build, selectedVariant, pairVariant) {
      if (this.coverageFor(key, build) !== 4) return Number(pairVariant.price);

      const configured = this.config.fourPieceAnchorRatios?.[key];
      const ratio = key === 'shoulder' ? configured?.[build.model] : configured;
      const numerator = Number(ratio?.numerator);
      const denominator = Number(ratio?.denominator);
      if (!Number.isSafeInteger(numerator) || numerator < 1) return Number(pairVariant.price);
      if (!Number.isSafeInteger(denominator) || denominator < numerator) return Number(pairVariant.price);

      return Math.floor(((Number(selectedVariant.price) * numerator) + Math.floor(denominator / 2)) / denominator);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents) / 100);
    }

    formatCompactMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(cents) / 100);
    }

    renderIncentives(calculations) {
      try {
        const build = this.activeBuild;
        if (!build) return;
        const index = this.state.builds.indexOf(build);
        const calc = calculations[index];
        this.querySelector('[data-cx-progress]')?.remove();
        this.querySelectorAll('[data-cx-hook]').forEach((node) => node.remove());
        this.querySelector('[data-cx-per-piece]')?.remove();
        if (!calc) return;

        const CORE_KEYS = ['thigh', 'waist', 'shoulder', 'cushion'];
        const availableCore = CORE_KEYS.filter((key) => moduleMode(key, build.model) === 'purchase');
        const selectedCore = availableCore.filter((key) => build.modules.has(key));
        const multiSet = this.state.builds.length > 1;
        const count = this.eligiblePiecesAcrossCart(build);
        const maxCount = multiSet ? 5 : Math.min(availableCore.length, 5);
        const pct = (bp) => (bp % 100 === 0 ? `${bp / 100}%` : `${(bp / 100).toFixed(1)}%`);
        const currentBp = this.coverageBasisPoints(Math.min(count, 5));

        if (this.summaryBuilds) {
          const steps = [];
          for (let n = 2; n <= maxCount; n += 1) steps.push({ count: n, bp: this.coverageBasisPoints(n) });
          const nextStep = steps.find((step) => step.count === count + 1);
          let headline;
          if (count <= 1) {
            headline = 'You are at full price. Two protection pieces unlock 10% off the whole set.';
          } else if (nextStep) {
            headline = multiSet
              ? `Unlocked ${pct(currentBp)} off across all sets. One more piece takes everything to ${pct(nextStep.bp)} off.`
              : `Unlocked ${pct(currentBp)} off. One more piece takes the whole set to ${pct(nextStep.bp)} off.`;
          } else {
            headline = multiSet
              ? `Fully unlocked at ${pct(currentBp)} off across all sets. Nothing left on the table.`
              : `Fully unlocked at ${pct(currentBp)} off. Nothing left on the table.`;
          }
          const segments = steps.map((step) => `
            <span class="cx-incentive-progress__step${count >= step.count ? ' is-on' : ''}">
              <em>${step.count} pieces</em><strong>${pct(step.bp)} off</strong>
            </span>`).join('');
          const bar = document.createElement('div');
          bar.className = 'cx-incentive-progress';
          bar.setAttribute('data-cx-progress', '');
          bar.innerHTML = `<p>${headline}</p><div class="cx-incentive-progress__track">${segments}</div>`;
          this.summaryBuilds.insertAdjacentElement('beforebegin', bar);
        }

        if (calc.valid) {
          availableCore.filter((key) => !build.modules.has(key)).forEach((key) => {
            const host = this.querySelector(`[data-module-card="${key}"] .cx-seat-builder__module-copy`);
            if (!host) return;
            const sim = {
              ...build,
              modules: new Set([...build.modules, key]),
              coverageByModule: { ...build.coverageByModule }
            };
            const simCalc = this.calculateBuild(sim, index);
            if (!simCalc.valid) return;
            const delta = simCalc.afterCoverageUnit - calc.afterCoverageUnit;
            const line = simCalc.lines.find((entry) => entry.key === key);
            const sticker = line ? Number(line.anchorPrice) : 0;
            if (!(delta > 0) || !(sticker > 0) || delta >= sticker) return;
            const offPct = Math.round((1 - delta / sticker) * 100);
            const hook = document.createElement('span');
            hook.className = 'cx-incentive-hook';
            hook.setAttribute('data-cx-hook', '');
            hook.innerHTML = `Add for <strong>+${this.formatCompactMoney(delta)}</strong>` +
              `<small>or ${this.formatCompactMoney(sticker)} on its own later</small>`;
            host.appendChild(hook);
          });
        }

        if (calc.valid && this.saveLine && !this.saveLine.hidden) {
          const pieces = calc.lines.reduce((sum, line) => sum + line.coverage, 0);
          if (pieces > 1) {
            const per = Math.round(calc.afterCoverageUnit / pieces / 100) * 100;
            const perLine = document.createElement('p');
            perLine.className = 'cx-incentive-per-piece';
            perLine.setAttribute('data-cx-per-piece', '');
            perLine.textContent = `Works out to about ${this.formatCompactMoney(per)} per piece for this set.`;
            this.saveLine.insertAdjacentElement('afterend', perLine);
          }
        }
      } catch (error) {
        /* Incentive layer is presentational only. It must never break the builder. */
      }
    }

    moduleLabel(key) {
      return this.products.get(key)?.label || key;
    }

    async submit(buyNow) {
      const calculations = this.state.builds.map((build, index) => this.calculateBuild(build, index));
      const order = this.calculateOrder(calculations);
      if (!order.valid || !this.config.cartEnabled || this.busy) return;

      const items = calculations.flatMap((calculation, index) => calculation.lines.map((line) => ({
        id: line.variant.id,
        quantity: calculation.build.quantity,
        properties: {
          '_cx_configuration_id': calculation.build.configurationId,
          '_cx_builder_version': this.config.version,
          '_cx_pricing_contract': this.config.pricingContract,
          '_cx_vehicle': calculation.build.model,
          '_cx_seat_color': calculation.build.color,
          '_cx_module': line.key,
          '_cx_coverage': line.coverage === 1 ? '1 pc' : `${line.coverage} pcs`,
          ...(line.key === 'waist' ? { '_cx_strap_style': calculation.build.strapStyle } : {}),
          ...(line.key === 'armrest' ? { '_cx_armrest_pattern_type': calculation.build.armrestSide } : {})
        }
      })));

      this.setBusy(true);
      this.hideError();

      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections }
        }));

        const config = window.theme?.utils?.fetchConfig
          ? window.theme.utils.fetchConfig('javascript')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        const route = window.theme?.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(route, {
          ...config,
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const parsedState = await response.json();
        if (!response.ok || parsedState.status) throw new Error(parsedState.description || 'The set could not be added to the cart.');

        this.state.builds.forEach((build) => {
          build.configurationId = this.createConfigurationId();
        });

        if (buyNow) {
          window.location.assign('/checkout');
          return;
        }

        const cartRoute = window.theme?.routes?.cart_url || '/cart.js';
        const cartConfig = window.theme?.utils?.fetchConfig
          ? window.theme.utils.fetchConfig('json', 'GET')
          : { method: 'GET', headers: { Accept: 'application/json' } };
        const cart = await (await fetch(cartRoute, cartConfig)).json();
        cart.sections = parsedState.sections;

        if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'seat-protection-builder',
            cart
          });
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
        document.querySelector('cart-drawer')?.show(this.addButton);
      } catch (error) {
        this.showError(`${error.message} You can still use the individual product links above.`);
      } finally {
        this.setBusy(false);
        this.renderSummary();
      }
    }

    setBusy(busy) {
      this.busy = busy;
      [this.addButton, this.buyButton, this.mobileAddButton].forEach((button) => {
        button.toggleAttribute('aria-busy', busy);
      });
      this.syncMobileBarVisibility();
    }

    showError(message) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    hideError() {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    showFallbackError(message) {
      const fallback = this.querySelector('[data-builder-fallback]');
      if (!fallback) return;
      const note = document.createElement('p');
      note.className = 'cx-seat-builder__fallback-error';
      note.textContent = message;
      fallback.prepend(note);
    }
  }

  if (!customElements.get('seat-protection-builder')) {
    customElements.define('seat-protection-builder', SeatProtectionBuilder);
  }
})();
